import {
  DetectedIdProductsGroup,
  FetchGroupedByDetectedIdProductsFilters,
  FetchGroupedByDetectedIdProductsLimit,
  FetchProductsCacheKey,
  FetchProductsGqlQuery
} from '../../productsTypes';
import { CacheFetchProductsQueryResponse } from '../../queries/fetchCacheProducts.query';

import { FETCH_CACHE_GROUPED_BY_DETECTED_ID_PRODUCTS_QUERY } from '../../queries/fetchCacheGroupedByDetectedIdProducts.query';

import { useCacheIndexQuery } from '../../../common/reactQuery/hooks/useCacheIndexQuery';

import { ProductCache } from '../../ProductCache';
import { ProductsPermissions } from '../../productsConstants';

export interface CacheGroupedByDetectedIdProductsOptions {
  cacheKey?: FetchProductsCacheKey;
  query?: FetchProductsGqlQuery;
  initialFilters: FetchGroupedByDetectedIdProductsFilters;
  initialLimit?: FetchGroupedByDetectedIdProductsLimit;
  options?: {
    cacheTime?: number;
    staleTime?: number;
    enabled?: boolean;
    enabledPlaceholder?: boolean;
    keepPreviousData?: boolean;
  };
}

const scope = 'groupedByDetectedIdProducts';

function useCacheGroupedByDetectedIdProducts<
  ProductItemType = CacheFetchProductsQueryResponse
>({
  cacheKey = ProductCache.groupedByDetectedIdProductsCacheKey(),
  query = FETCH_CACHE_GROUPED_BY_DETECTED_ID_PRODUCTS_QUERY,
  initialFilters,
  initialLimit = 10,
  options = {}
}: CacheGroupedByDetectedIdProductsOptions) {
  const {
    data,
    items,
    itemsError,
    isFetched,
    isLoading,
    isPlaceholderData,
    currentFilters,
    currentLimit,
    filterItems,
    changeItemsFilters,
    limitItems
  } = useCacheIndexQuery<DetectedIdProductsGroup<ProductItemType>>({
    action: ProductsPermissions.READ_PRODUCTS_INDEX_ES_QUERY,
    defaultApi: 'cache_api',
    scope,
    query,
    cacheQuery: query,
    cacheKey,
    initialFilters,
    initialLimit,
    options: { ...options, withoutPrefetch: true }
  });

  const filterGroupedByDetectedIdProducts: (
    filters: FetchGroupedByDetectedIdProductsFilters
  ) => void = filterItems;
  const changeGroupedByDetectedIdProductsFilters: (
    filters: Partial<FetchGroupedByDetectedIdProductsFilters>
  ) => void = changeItemsFilters;
  const limitGroupedByDetectedIdProducts: (
    limit: FetchGroupedByDetectedIdProductsLimit
  ) => void = limitItems;

  return {
    groupedByDetectedIdProductsData: data,
    groupedByDetectedIdProducts: items,
    groupedByDetectedIdProductsError: itemsError,
    groupedByDetectedIdProductsFetched: isFetched,
    groupedByDetectedIdProductsLoading: isLoading,
    groupedByDetectedIdProductsIsPlaceholderData: isPlaceholderData,
    groupedByDetectedIdProductsFilters:
      currentFilters as FetchGroupedByDetectedIdProductsFilters,
    groupedByDetectedIdProductsLimit: currentLimit,
    filterGroupedByDetectedIdProducts,
    changeGroupedByDetectedIdProductsFilters,
    limitGroupedByDetectedIdProducts
  };
}

export default useCacheGroupedByDetectedIdProducts;
